(() => {
  function fileBasenameFromUrl(url) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.href);
      const seg = (u.pathname || '').split('/').pop() || '';
      return decodeURIComponent(seg.replace(/\+/g, ' '));
    } catch {
      return '';
    }
  }

  /**
   * Files-mode slides must resolve to a CDN basename starting with the configured prefix
   * (e.g. gallery-ibiza2025-042.webp). Drops slides that do not match.
   */
  function removeFileSlidesNotMatchingPrefix(root, track) {
    if (!root || root.dataset.sacbeEnforceFilePrefix !== 'true') return;
    const prefix = (root.dataset.sacbeFilenamePrefix || '').trim();
    if (!prefix) return;
    const digits = String(root.dataset.sacbeFileIndexDigits || '3');

    const slides = Array.from(track.querySelectorAll('[data-sacbe-file-slide]'));
    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) {
        slide.remove();
        return;
      }
      const src = img.currentSrc || img.src || img.getAttribute('src') || '';
      const actual = fileBasenameFromUrl(src);
      const prefixOk =
        actual.length >= prefix.length &&
        actual.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase();
      if (!prefixOk) {
        slide.remove();
        return;
      }
      // Only numbered strips: prefix-001.ext — avoids unrelated Files that share the prefix.
      const rest = actual.slice(prefix.length);
      const idxRe =
        digits === '2' ? /^-(\d{2})\./i : digits === '4' ? /^-(\d{4})\./i : /^-(\d{3})\./i;
      if (!idxRe.test(rest)) slide.remove();
    });
  }

  /** Drops portrait / square slides (typical lineup headshots); keeps clear landscape. */
  function removeNonLandscapeFileSlides(root, track) {
    if (!root || root.dataset.sacbeFilesLandscapeOnly !== 'true') return;
    const slides = Array.from(track.querySelectorAll('[data-sacbe-file-slide]'));
    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img || !slide.isConnected) return;
      if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w <= h * 1.06) slide.remove();
    });
  }

  /** Removes slides whose image failed (404, etc.). Browser may still log network errors — expected for sparse galleries. */
  function removeFailedFileSlides(track) {
    if (!track) return;
    track.querySelectorAll('[data-sacbe-file-slide] img').forEach((img) => {
      if (!img.complete) return;
      if (img.naturalWidth === 0 && img.naturalHeight === 0) {
        img.closest('[data-sacbe-file-slide]')?.remove();
      }
    });
  }

  function sleepMs(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Row strips use many off-screen images with loading="lazy". Those often never intersect the
   * viewport inside overflow:hidden, so load never fires — waiting for all images would hang forever
   * and the marquee never gets `is-marquee-active`. Cap wait so animation always starts.
   */
  function waitForRowImages(track, maxWaitMs = 5000) {
    const imgs = Array.from(track.querySelectorAll('[data-sacbe-file-slide] img'));
    const pending = Promise.all(
      imgs.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              }),
      ),
    );
    return Promise.race([pending, sleepMs(maxWaitMs)]);
  }

  function shuffleChildren(container) {
    const nodes = Array.from(container.children);
    for (let i = nodes.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nodes[i], nodes[j]] = [nodes[j], nodes[i]];
    }
    const frag = document.createDocumentFragment();
    nodes.forEach((n) => frag.appendChild(n));
    container.appendChild(frag);
  }

  function duplicateTrackForMarquee(track) {
    const originals = Array.from(track.children);
    originals.forEach((node) => {
      const c = node.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      track.appendChild(c);
    });
  }

  class SacbeIbizaEventsSlider extends HTMLElement {
    connectedCallback() {
      const layout = this.dataset.sacbeLayout || 'fade';
      if (layout === 'row') {
        this.initRowStrip();
        return;
      }
      this.initFadeCarousel();
    }

    initRowStrip() {
      const track = this.querySelector('[data-sacbe-events-row-track]');
      if (!track || track.children.length === 0) return;

      removeFileSlidesNotMatchingPrefix(this, track);
      if (track.children.length === 0) return;

      const imgs = Array.from(track.querySelectorAll('.sacbe-ibiza-events-slider__slide--row img'));
      imgs.forEach((img) => {
        img.addEventListener(
          'error',
          () => {
            img.closest('[data-sacbe-file-slide]')?.remove();
            img.closest('[data-sacbe-events-slide]')?.remove();
          },
          { once: true },
        );
      });

      const settleAndRun = async () => {
        removeFileSlidesNotMatchingPrefix(this, track);

        // Native lazy-load often skips images that sit off-screen in this horizontal strip
        // (overflow hidden). Switch to eager so slides fetch and the marquee can measure / run.
        track.querySelectorAll('[data-sacbe-file-slide] img').forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
        });

        await waitForRowImages(track);

        removeFailedFileSlides(track);
        removeFileSlidesNotMatchingPrefix(this, track);
        removeNonLandscapeFileSlides(this, track);

        await new Promise((r) => requestAnimationFrame(r));
        removeFailedFileSlides(track);

        const imgsLive = Array.from(track.querySelectorAll('.sacbe-ibiza-events-slider__slide--row img'));
        imgsLive.forEach((img) => {
          const slide = img.closest('[data-sacbe-events-slide]');
          if (!slide || !slide.isConnected) return;
          if (img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
            slide.remove();
          }
        });
        if (track.children.length === 0) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        duplicateTrackForMarquee(track);

        const root = track.closest('sacbe-ibiza-events-slider');
        const applyDuration = () => {
          if (!root || track.scrollWidth <= 0) return false;
          const baseSec = Math.max(
            40,
            parseFloat(root.dataset.marqueeBaseSec || root.dataset.marqueeDuration || '180', 10) || 180,
          );
          const w = track.scrollWidth;
          const TARGET_PX_PER_SEC = 18;
          const adaptiveSec = (w * 0.5) / TARGET_PX_PER_SEC;
          const durationSec = Math.max(baseSec, adaptiveSec);
          if (Number.isFinite(durationSec) && durationSec > 0) {
            root.style.setProperty('--sacbe-marquee-duration', `${durationSec}s`);
          }
          return true;
        };

        let attempts = 0;
        const maxAttempts = 20;
        const activateWhenLaidOut = () => {
          attempts += 1;
          applyDuration();
          if (track.scrollWidth > 0 || attempts >= maxAttempts) {
            if (!reduceMotion) {
              track.classList.add('is-marquee-active');
            }
            return;
          }
          requestAnimationFrame(activateWhenLaidOut);
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(activateWhenLaidOut);
        });

        // Lazy images that load after the capped wait widen the track — refresh duration & layout.
        track.addEventListener(
          'load',
          () => {
            applyDuration();
          },
          true,
        );
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          settleAndRun();
        });
      });
    }

    initFadeCarousel() {
      this.viewport = this.querySelector('[data-sacbe-events-viewport]');
      if (!this.viewport) return;

      this.slides = Array.from(this.viewport.querySelectorAll('[data-sacbe-events-slide]'));
      if (this.slides.length === 0) return;

      shuffleChildren(this.viewport);

      this.slides = Array.from(this.viewport.querySelectorAll('[data-sacbe-events-slide]'));
      this.index = 0;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const intervalMs = Math.max(2000, parseInt(this.dataset.intervalMs || '5000', 10));

      this.dots = Array.from(this.querySelectorAll('[data-sacbe-events-dot]'));
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === 0);
        slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
      });
      this.syncDots();

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i);
          if (!this.reduceMotion && this.pause && this.play) {
            this.pause();
            window.setTimeout(() => this.play(), parseInt(this.dataset.intervalMs || '5000', 10) * 2);
          }
        });
      });

      if (this.slides.length < 2) return;

      if (this.reduceMotion) {
        return;
      }

      this.timer = null;
      const tick = () => {
        this.goTo((this.index + 1) % this.slides.length);
      };

      this.play = () => {
        if (this.timer) clearInterval(this.timer);
        this.timer = setInterval(tick, intervalMs);
      };

      this.pause = () => {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      };

      this.play();

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.play();
      });
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    goTo(next) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');

      this.index = next;

      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'false');
      this.syncDots();
    }

    syncDots() {
      if (!this.dots.length) return;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('sacbe-ibiza-events-slider')) {
    customElements.define('sacbe-ibiza-events-slider', SacbeIbizaEventsSlider);
  }
})();
